import axiosInstance from "./axiosInstance";

function authHeader(token?: string) {
    if (!token || token.trim().length === 0) return {};
    return { Authorization: `Bearer ${token}` };
}

export type ContactMethod = "EMAIL" | "PHONE" | "TEXT" | "WHATSAPP" | "IN_PERSON" | "OTHER";

export interface Prospect {
    id: number;
    name: string | null;
    orgName: string | null;
    email: string | null;
    phone: string | null;
    /** Line type from the AWS lookup (MOBILE/LANDLINE/VOIP/...); null = not
     *  checked. Landlines can't receive SMS — the panel greys text buttons. */
    phoneType: string | null;
    phoneCarrier: string | null;
    /** The business's web domain ("actionis.com") — optional. */
    domain: string | null;
    /** Their contact page URL — optional; where to look when the stored
     *  email/phone turn out to be generic front-desk contacts. */
    contactUrl: string | null;
    category: string | null;
    /** Matched category-tree id (null when the name has no match yet). */
    categoryId: string | null;
    /** The matched category's hasServiceArea flag — the compose dialog uses
     *  the service-flavored template pair when true. */
    serviceCategory: boolean;
    /** Geodata id chain, one field per level; any level may be null below the
     *  deepest selection. Enables hierarchical filtering (state ⊇ its cities). */
    countryId: number | null;
    admin1Id: number | null;
    admin2Id: number | null;
    cityId: number | null;
    /** Display text ("Austin, Travis County, Texas, United States"). */
    location: string | null;
    notes: string | null;
    contacted: boolean;
    contactMethod: ContactMethod | null;
    contactedAt: string | null;
    joined: boolean;
    joinedAt: string | null;
    /** Organization created when the prospect registered (null until joined). */
    orgId: number | null;
    createdAt: string | null;
    /** They opened an invite email (SES tracking pixel; approximate). */
    opened: boolean;
    openedAt: string | null;
    /** They clicked a link in an invite email (visited the site). */
    clicked: boolean;
    clickedAt: string | null;
    /** Their current listing on a keyword site (craigslist/eBay), for the
     *  personalized /why/p page; title/image fetched server-side. */
    currentListingUrl: string | null;
    currentListingTitle: string | null;
    currentListingImageUrl: string | null;
    /** Token of the personalized why page (/why/p/{token}); public but
     *  unguessable, used by the {why_link} placeholder. */
    whyToken: string | null;
    /** Token of the claim-your-org link (/claim/{token}); set when a managed
     *  org exists for this prospect. Used by the {claim_link} placeholder. */
    claimToken: string | null;
    /** Pending claim request from the public listing banner (visitor left
     *  their email because the stored contact is a shared inbox) — the admin
     *  verifies, then approves (sends the link there) or dismisses. */
    claimRequestName: string | null;
    claimRequestEmail: string | null;
    claimRequestAt: string | null;
    /** Per-prospect why-page overrides; null = the global templates. */
    whyHeading: string | null;
    whyPoints: string | null;
    /** An uploaded screenshot of their listing exists (shown on the why page
     *  in preference to the fetched preview). */
    hasScreenshot: boolean;
    /** An invite email permanently bounced (dead address); sending is
     *  blocked until the address is edited, which clears the flag. */
    bounced: boolean;
    bouncedAt: string | null;
    /** They clicked the unsubscribe link — no more invite emails allowed. */
    unsubscribed: boolean;
    unsubscribedAt: string | null;
}

/** One outreach contact event (a prospect can have many). */
export interface ProspectContactEvent {
    id: number;
    prospectId: number;
    method: ContactMethod | null;
    contactedAt: string | null;
    /** Email subject for EMAIL contacts; null otherwise. */
    subject: string | null;
}

/** Contact history of a prospect, newest first. */
export async function getProspectContacts(token: string, id: number): Promise<ProspectContactEvent[]> {
    const resp = await axiosInstance.get<ProspectContactEvent[]>(`/prospects/${id}/contacts`, {
        headers: authHeader(token),
    });
    return resp.data ?? [];
}

/** One CSV row for bulk import; state/country accept names or codes.
 *  domain/contactUrl are optional columns. */
export interface ImportRow {
    name: string; orgName: string; email: string; phone: string;
    category: string; city: string; state: string; country: string; notes: string;
    domain?: string; contactUrl?: string;
}

export interface ImportFailure { identifier: string; reason: string; }

export interface ImportReport {
    added: number;
    alreadyMembers: number;
    duplicates: number;
    failed: ImportFailure[];
}

/** Bulk import; each row's location is resolved to geo ids server-side. */
export async function importProspects(token: string, rows: ImportRow[]): Promise<ImportReport> {
    const resp = await axiosInstance.post<ImportReport>("/prospects/import", rows, {
        headers: authHeader(token),
    });
    return resp.data;
}

/** The invite templates (from-address plus two subject/message pairs, with
 *  {name}/{company}/{category} placeholders): the regular pair for product
 *  categories and the service pair for categories flagged hasServiceArea.
 *  Stored in the DB, editable by OUTREACH_MANAGE, live without restarts. */
export interface InviteTemplate {
    fromEmail: string;
    subject: string;
    message: string;
    serviceSubject: string;
    serviceMessage: string;
    /** How many options of a choice parameter the {features} list shows
     *  (stored as text like the other settings; server validates 1-99). */
    featureChoiceCount: string;
    /** SHORT message used for text/WhatsApp — carriers refuse very long
     *  multi-part SMS, so this stays a few sentences. */
    smsMessage: string;
    /** Why-page texts (the live form-first design): the top pitch, the
     *  dropdown nudge, the two find-you-by-place rules, and the closing
     *  "searching uses the same form" paragraph. */
    whyPitch: string;
    whyFormNudge: string;
    whyPlaceService: string;
    whyPlaceLocation: string;
    whySameForm: string;
    /** The claim-your-listing email ({company}/{claim_link}) and the
     *  comma-separated shared-inbox local parts that are NOT trusted for
     *  one-click claim links. */
    claimSubject: string;
    claimMessage: string;
    genericEmailLocals: string;
}

/** The public why-page texts (no auth — the pages render for prospects).
 *  pitch/formNudge/place/sameForm drive the live form-first page;
 *  heading/points drive the original page parked at /why0. */
export interface WhyTexts {
    heading: string;
    points: string;
    pointsService: string;
    pitch: string;
    formNudge: string;
    placeService: string;
    placeLocation: string;
    sameForm: string;
}

export async function getWhyTexts(): Promise<WhyTexts> {
    const resp = await axiosInstance.get<WhyTexts>("/public/why-texts");
    return resp.data;
}

export async function getInviteTemplate(token: string): Promise<InviteTemplate> {
    const resp = await axiosInstance.get<InviteTemplate>("/prospects/invite-template", {
        headers: authHeader(token),
    });
    return resp.data;
}

/** The built-in defaults (compiled into the server) — what a blanked field
 *  reverts to. Shown read-only in the template editor for reference. */
export async function getInviteTemplateDefaults(token: string): Promise<InviteTemplate> {
    const resp = await axiosInstance.get<InviteTemplate>("/prospects/invite-template/defaults", {
        headers: authHeader(token),
    });
    return resp.data;
}

/** Blank fields revert to the built-in defaults. Returns the effective template. */
export async function saveInviteTemplate(token: string, t: InviteTemplate): Promise<InviteTemplate> {
    const resp = await axiosInstance.put<InviteTemplate>("/prospects/invite-template", t, {
        headers: authHeader(token),
    });
    return resp.data;
}

/** Fields the admin fills in; all status fields (contacted, joined,
 *  unsubscribed, clicked) are managed entirely by the backend. Category is
 *  required. Location is optional at every depth: empty = unknown/no place,
 *  country-only = a national business, deeper = a real place. */
export type ProspectDraft = Pick<
    Prospect,
    "name" | "orgName" | "email" | "phone" | "domain" | "contactUrl"
    | "category" | "notes"
    | "countryId" | "admin1Id" | "admin2Id" | "cityId" | "location"
    | "currentListingUrl" | "whyHeading" | "whyPoints"
>;

/** Public data behind /why/p/{token} (no auth; token-gated). */
export interface WhyPageData {
    orgName: string | null;
    category: string | null;
    categoryId: string | null;
    listingUrl: string | null;
    listingTitle: string | null;
    listingImageUrl: string | null;
    listingDescription: string | null;
    hasScreenshot: boolean;
    /** Per-prospect overrides (null = global templates). */
    whyHeading: string | null;
    whyPoints: string | null;
    /** The prospect's geo chain — pre-fills the demo form's location picker. */
    countryId: number | null;
    admin1Id: number | null;
    admin2Id: number | null;
    cityId: number | null;
    /** Set only while their managed org awaits claiming — drives the
     *  "your listing page is already live, take ownership" banner. */
    claimToken: string | null;
}

/** Saves a prospect's why-page override from the page itself (admin only;
 *  keyed by the page's why token; blank fields clear the override). */
export async function saveWhyOverride(
    authToken: string,
    whyToken: string,
    override: { whyHeading: string; whyPoints: string }
): Promise<void> {
    await axiosInstance.put(`/prospects/why-override/${whyToken}`, override, {
        headers: authHeader(authToken),
    });
}

/** "I get it" / "I don't get it" from a why page (public — prospects are
 *  signed out). The server resolves token → prospect. */
export async function sendWhyFeedback(fb: {
    token?: string | null;
    categoryId?: string | null;
    verdict: "GOT_IT" | "DONT_GET_IT";
    comment: string;
}): Promise<void> {
    await axiosInstance.post("/public/why-feedback", fb);
}

/** One feedback entry; prospect fields resolved when the page was /why/p. */
export interface WhyFeedbackEntry {
    id: number;
    verdict: string;
    comment: string | null;
    prospectId: number | null;
    orgName: string | null;
    category: string | null;
    createdAt: string | null;
}

export async function listWhyFeedback(token: string): Promise<WhyFeedbackEntry[]> {
    const resp = await axiosInstance.get<WhyFeedbackEntry[]>("/prospects/why-feedback", {
        headers: authHeader(token),
    });
    return resp.data ?? [];
}

export async function getWhyPageData(token: string): Promise<WhyPageData> {
    const resp = await axiosInstance.get<WhyPageData>(`/public/why/${token}`);
    return resp.data;
}

/** Absolute URL of the why-page screenshot (public, token-gated). */
export function whyScreenshotUrl(token: string): string {
    const base = (axiosInstance.defaults.baseURL ?? "").replace(/\/+$/, "");
    return `${base}/public/why/${token}/screenshot`;
}

/** Uploads (replaces) the screenshot of the prospect's current listing. */
export async function uploadProspectScreenshot(
    token: string,
    id: number,
    file: File
): Promise<Prospect> {
    const form = new FormData();
    form.append("file", file);
    const resp = await axiosInstance.post<Prospect>(`/prospects/${id}/screenshot`, form, {
        headers: { ...authHeader(token), "Content-Type": "multipart/form-data" },
    });
    return resp.data;
}

/** Line type of a phone number (nothing stored — saving the prospect stores it). */
export async function checkPhoneType(
    token: string,
    phone: string
): Promise<{ phoneType: string; carrier: string | null }> {
    const resp = await axiosInstance.post<{ phoneType: string; carrier: string | null }>(
        "/prospects/phone-type",
        { phone },
        { headers: authHeader(token) }
    );
    return resp.data;
}

/** Bulk line-type backfill for every prospect with an unchecked phone. */
export async function checkAllPhoneTypes(
    token: string
): Promise<{ checked: number; typed: number; unknown: number }> {
    const resp = await axiosInstance.post<{ checked: number; typed: number; unknown: number }>(
        "/prospects/check-phone-types", {}, { headers: authHeader(token) }
    );
    return resp.data;
}

/** Forces a re-fetch of the listing's og preview (same URL). */
export async function refreshProspectListingPreview(token: string, id: number): Promise<Prospect> {
    const resp = await axiosInstance.post<Prospect>(`/prospects/${id}/refresh-listing-preview`, {}, {
        headers: authHeader(token),
    });
    return resp.data;
}

export async function deleteProspectScreenshot(token: string, id: number): Promise<Prospect> {
    const resp = await axiosInstance.delete<Prospect>(`/prospects/${id}/screenshot`, {
        headers: authHeader(token),
    });
    return resp.data;
}

/** Creates the managed org (+ default store, contact, location) for the
 *  prospect; BabaHanks stewards it until the business claims it. */
export async function createManagedOrg(token: string, id: number): Promise<Prospect> {
    const resp = await axiosInstance.post<Prospect>(`/prospects/${id}/create-org`, {}, {
        headers: authHeader(token),
    });
    return resp.data;
}

/** A short-lived token scoped to the prospect's managed org — the listing
 *  workspace works with it unchanged. Refused once the org is claimed. */
export async function actAsOrg(
    token: string,
    id: number
): Promise<{ token: string; orgName: string; expiresIn: number }> {
    const resp = await axiosInstance.post<{ token: string; orgName: string; expiresIn: number }>(
        `/prospects/${id}/act-as-org`, {}, { headers: authHeader(token) }
    );
    return resp.data;
}

/** The claim page's info (public): org name + masked email. */
export async function getClaimInfo(
    claimToken: string
): Promise<{ orgName: string; maskedEmail: string; claimable: boolean }> {
    const resp = await axiosInstance.get<{ orgName: string; maskedEmail: string; claimable: boolean }>(
        `/public/claim/${claimToken}`
    );
    return resp.data;
}

/** Claims the org: creates the business's first user on it (public). */
export async function claimOrg(claimToken: string, userName: string, password: string): Promise<void> {
    await axiosInstance.post(`/public/claim/${claimToken}`, { userName, password });
}

/** Whether a listing's org is an unclaimed managed one (public — no token is
 *  ever exposed here). mode "send" = trusted contact on file, one click mails
 *  the link there; mode "request" = shared-inbox/missing contact, the visitor
 *  leaves their email for admin verification instead. */
export async function getOrgClaimStatus(
    orgId: number
): Promise<{ unclaimed: boolean; mode: "send" | "request" | null; maskedEmail: string | null }> {
    const resp = await axiosInstance.get<{ unclaimed: boolean; mode: "send" | "request" | null; maskedEmail: string | null }>(
        `/public/claim-status/${orgId}`
    );
    return resp.data;
}

/** Emails the claim link to the business's address on file (public). */
export async function sendClaimLink(orgId: number): Promise<void> {
    await axiosInstance.post(`/public/claim-status/${orgId}/send-link`, {});
}

/** Leaves the visitor's own email for the ownership link (public); the admin
 *  verifies before anything is sent. */
export async function requestClaimLink(orgId: number, name: string, email: string): Promise<void> {
    await axiosInstance.post(`/public/claim-status/${orgId}/request`, { name, email });
}

/** Approve the pending claim request: sends the claim link to the requested
 *  address and clears the request. */
export async function approveClaimRequest(token: string, id: number): Promise<Prospect> {
    const resp = await axiosInstance.post<Prospect>(`/prospects/${id}/approve-claim-request`, {}, {
        headers: authHeader(token),
    });
    return resp.data;
}

/** Dismiss the pending claim request without sending anything. */
export async function dismissClaimRequest(token: string, id: number): Promise<Prospect> {
    const resp = await axiosInstance.post<Prospect>(`/prospects/${id}/dismiss-claim-request`, {}, {
        headers: authHeader(token),
    });
    return resp.data;
}

export async function listProspects(token: string): Promise<Prospect[]> {
    const resp = await axiosInstance.get<Prospect[]>("/prospects", { headers: authHeader(token) });
    return resp.data ?? [];
}

export async function createProspect(token: string, draft: ProspectDraft): Promise<Prospect> {
    const resp = await axiosInstance.post<Prospect>("/prospects", draft, { headers: authHeader(token) });
    return resp.data;
}

export async function updateProspect(token: string, id: number, draft: ProspectDraft): Promise<Prospect> {
    const resp = await axiosInstance.put<Prospect>(`/prospects/${id}`, draft, { headers: authHeader(token) });
    return resp.data;
}

export async function deleteProspect(token: string, id: number): Promise<void> {
    await axiosInstance.delete(`/prospects/${id}`, { headers: authHeader(token) });
}

/** Sends the invite email and marks the prospect contacted (EMAIL).
 *  resend=true sends again to an already-contacted prospect (unsubscribed
 *  and joined stay blocked server-side regardless). */
export async function contactProspectByEmail(
    token: string,
    id: number,
    subject: string,
    message: string,
    resend: boolean = false
): Promise<Prospect> {
    const resp = await axiosInstance.post<Prospect>(
        `/prospects/${id}/contact`,
        { subject, message, resend },
        { headers: authHeader(token) }
    );
    return resp.data;
}

/** Records a contact that happened outside the app (phone, text, in person). */
export async function markProspectContacted(
    token: string,
    id: number,
    method: ContactMethod
): Promise<Prospect> {
    const resp = await axiosInstance.post<Prospect>(
        `/prospects/${id}/mark-contacted`,
        { method },
        { headers: authHeader(token) }
    );
    return resp.data;
}